<template>
  <div class="uk-margin-top history">
    <div class="uk-card uk-card-default uk-width-large uk-margin-auto">
      <div class="uk-card-header uk-text-center">
        <h3 class="uk-card-title">History</h3>
        <p class="uk-text-meta">Logs - User Actions</p>
      </div>

      <div class="uk-card-body uk-padding-remove uk-text-center actions">
        <div v-for="(action, index) in actions" :key="index" class="action">
          <div class="description" :class="{ hover: action.details }">
            <div class="message">
              <span v-html="action.message.primary" />

              <template v-if="action.message.secondary">
                - <span v-html="action.message.secondary" />
              </template>
            </div>

            <div class="info uk-text-meta">
              <span :uk-tooltip="`title: ${action.info.timestamp.default}; pos: bottom;`">{{ action.info.timestamp.human }}</span> - <b>{{ action.info.user }}</b>
            </div>

            <div v-if="action.details" class="icon"><div class="icon gg-info" /></div>
          </div>

          <div v-if="action.details" class="details uk-width-large" uk-drop="mode: hover; pos: top-center; delay-hide: 0; offset: 1; animation: uk-animation-slide-bottom;" v-html="action.details" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ComponentAppointmentHistory',

  props: {
    log: {
      type: Array,
      required: true
    }
  },

  computed: {
    actions() {
      const actions = []
      // Slice is used to make a shallow copy of an array to prevent state mutation and infinite loop.
      const log = this.log.slice().reverse()

      log.forEach((action) => {
        const { type } = action
        const { subtype } = action
        const user = this.getUser(action.user)
        const refund = action.content && action.content.refund
        const message = {}
        let details = null
        const info = {
          timestamp: {
            human: this.$moment(action.timestamp.seconds, 'X').fromNow(),
            default: this.$moment(action.timestamp.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss')
          },
          user
        }

        if (type === 'create') {
          message.primary = 'Appointment created'
        }

        if (type === 'update') {
          if (subtype && subtype === 'appointment-payment') {
            let status = null
            switch (action.content.status) {
            case 'succeeded':
              status = 'Success'
              break
            default:
              status = null
            }

            message.primary = 'Payment updated'
            message.secondary = `<b>${status}</b>.`
            // details = `Payment was updated on <b>${this.$moment(action.timestamp.seconds, 'X').format('DD/MM/YYYY - HH:mm:ss')}</b> <br> by <b>${user}</b>.`
          }

          if (subtype && subtype === 'appointment-dentist') {
            const od = action.content.original
            const nd = action.content.new

            const difference = this.compare(od, nd)
            const changes = []

            difference.forEach((item) => {
              let change = null

              if (item === 'bit') {
                change = `The time slot was changed from <b>${this.$helpers.timeBits.find((b) => b.id === od.bit).time.short}</b> to <b>${this.$helpers.timeBits.find((b) => b.id === nd.bit).time.short}</b>.<br>`
              }

              if (item === 'date') {
                change = `The date was changed from <b>${this.$moment(od.date, 'YYMMDD').format('DD/MM/YYYY')}</b> to <b>${this.$moment(nd.date, 'YYMMDD').format('DD/MM/YYYY')}</b>.<br>`
              }

              if (item === 'dentist') {
                change = `Dentist was changed from <b>${this.$store.getters['dentists/find'](od.dentist).name}</b> to <b>${this.$store.getters['dentists/find'](nd.dentist).name}</b>.<br>`
              }

              if (item === 'location') {
                change = `Customer location was changed from <b>${od.location}</b> to <b>${nd.location}</b>.<br>`
              }

              changes.push(change)
            })

            message.primary = 'Appointment updated'
            message.secondary = (difference.length === 1) ? `<b>${difference.length}</b> change.` : `<b>${difference.length}</b> changes.`

            details = changes.join('')

            if (refund) {
              message.secondary = message.secondary.concat(' <b>With refund</b>.')
              details = details.concat(` Refund with value of <b>£${(refund.value / 100).toFixed(2)}</b> was requested.<br> Refund reason: ${refund.reason}.`)
            }
          }
        }

        if (type === 'cancel') {
          message.primary = 'Appointment cancelled'
          message.secondary = (refund) ? '<b>With refund</b>.' : '<b>Without refund</b>.'

          if (refund) {
            details = `Refund with value of <b>£${(refund.value / 100).toFixed(2)}</b> was requested.<br> Refund reason: ${refund.reason}.`
          }
        }

        if (type === 'notification') {
          const notifications = []
          let typeMessage = null

          if (subtype && subtype === 'create') {
            typeMessage = 'Booking Notifications'
            if (action.content.customer.email.status) notifications.push('Customer Notification - Email - Appointment Booked')
            if (action.content.customer.phone.status === 'accepted') notifications.push('Customer Notification - SMS - Appointment Booked')
            if (action.content.dentist.email && action.content.dentist.email.status) notifications.push('Dentist Notification - Email - Appointment Booked')
            if (action.content.dentist.phone && action.content.dentist.phone.status === 'accepted') notifications.push('Dentist Notification - SMS - Appointment Booked')
          }

          if (subtype && subtype === 'update') {
            typeMessage = 'Update Notifications'
            if (action.content.customer.email.status) notifications.push('Customer Notification - Email - Appointment Updated')
            if (action.content.customer.phone.status === 'accepted') notifications.push('Customer Notification - SMS - Appointment Updated')
            if (action.content.dentist.email && action.content.dentist.email.status) notifications.push('Dentist Notification - Email - Appointment Updated')
            if (action.content.dentist.phone && action.content.dentist.phone.status === 'accepted') notifications.push('Dentist Notification - SMS - Appointment Updated')
            if (action.content.dentist.original && action.content.dentist.original.email.status) notifications.push('Original Dentist Notification - Email - Appointment Cancelled')
            if (action.content.dentist.original && action.content.dentist.original.phone.status === 'accepted') notifications.push('Original Dentist Notification - SMS - Appointment Cancelled')
            if (action.content.dentist.new && action.content.dentist.new.email.status) notifications.push('New Dentist Notification - Email - Appointment Booked')
            if (action.content.dentist.new && action.content.dentist.new.phone.status === 'accepted') notifications.push('New Dentist Notification - Email - Appointment Booked')
          }

          if (subtype && subtype === 'cancel') {
            typeMessage = 'Cancel Notifications'
            if (action.content.customer.email.status) notifications.push('Customer Notification - Email - Appointment Cancelled')
            if (action.content.customer.phone.status === 'accepted') notifications.push('Customer Notification - SMS - Appointment Cancelled')
            if (action.content.dentist.email && action.content.dentist.email.status) notifications.push('Dentist Notification - Email - Appointment Cancelled')
            if (action.content.dentist.phone && action.content.dentist.phone.status === 'accepted') notifications.push('Dentist Notification - SMS - Appointment Cancelled')
          }


          const amount = notifications.length

          message.primary = typeMessage
          message.primary = message.primary.concat((amount > 1) ? ` - <b>${amount}</b> notifications sent.` : ` - <b>${amount}</b> notification sent.`)
          details = notifications.join('<br>')
        }

        actions.push({ info, message, details })
      })

      return actions
    }
  },

  methods: {
    getUser(id) {
      let user = null

      if (id === 'server') {
        user = {
          name: {
            first: 'Automatic',
            last: '(Server)'
          }
        }
      } else {
        user = this.$store.getters['users/find'](id)
      }
      return `${user.name.first} ${user.name.last}`
    },

    compare(object, base) {
      const diff = []
      Object.keys(object).forEach((key) => {
        if (base[key] !== object[key]) {
          diff.push(key)
        }
      })
      return diff
    }

  }
}
</script>
